/**
 * Phase 29 — Partner Success Pack admin page.
 *
 * Route: /admin/partners/success-pack
 *
 * PINNED USER RULE (Feb 2026):
 *   Preview / download / reference only. No emails, SMS, social
 *   posts, partner notifications, customer notifications, ServiceM8
 *   jobs, calendar tasks, commission / pricing / white-label /
 *   partner-status / preferred-status / public-allocation changes.
 *   Re-uses Phase 19 QR endpoints and Phase 25 disclosure
 *   catalogue.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Copy, Loader2, ShieldCheck, AlertTriangle, Image as ImageIcon,
  FileText, Phone, Mail, Building2, Share2, User,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const SCRIPT_ICON = {
  phone: Phone,
  email: Mail,
  property_manager: Building2,
  social_post: Share2,
  in_person: User,
};


function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Copy failed");
  }
}


export default function PWSSuccessPack() {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [pack, setPack] = useState(null);
  const [loadingPack, setLoadingPack] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pws-success-pack/list");
      setPartners(r.data.rows || []);
      if (!selectedId && (r.data.rows || []).length > 0) {
        setSelectedId(r.data.rows[0].id);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadPack = useCallback(async () => {
    if (!selectedId) {
      setPack(null);
      return;
    }
    setLoadingPack(true);
    try {
      const r = await api.get(`/admin/pws-success-pack/${selectedId}`);
      setPack(r.data);
    } catch (err) {
      toast.error("Failed to load success pack");
    } finally {
      setLoadingPack(false);
    }
  }, [selectedId]);

  useEffect(() => { loadPack(); }, [loadPack]);

  const qrPngHref = useMemo(() => {
    if (!pack) return null;
    // Prefer the inlined data URL (Phase 19 "one QR truth") so the
    // <img> tag and the Download links work without needing to
    // attach a Bearer token. Fall back to the auth-required admin
    // endpoint only when the backend somehow omits the data URL.
    return pack.qr_endpoints?.png_data_url || null;
  }, [pack]);

  const qrSvgHref = useMemo(() => {
    if (!pack) return null;
    return pack.qr_endpoints?.svg_data_url || null;
  }, [pack]);

  if (loading && partners.length === 0) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div
        data-testid="ph29-success-pack-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-7"
      >
        <div>
          <Link
            to="/admin/partners"
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Partners
          </Link>
          <h1
            data-testid="ph29-page-title"
            className="font-display mt-2 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Partner Success Pack
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Preview, reference and download materials for active PWS
            partners. Reuses your Phase 19 QR code and Phase 25
            consent wording. No notifications are sent from this page.
          </p>
        </div>

        <div
          data-testid="ph29-safety-banner"
          className="flex items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-sm text-emerald-900"
        >
          <ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
          <div>
            <div className="font-semibold">
              Preview / download / reference only.
            </div>
            <div className="mt-0.5">
              No emails, SMS, social posts or partner notifications
              are sent from this page. Re-uses the Phase 19 QR code
              and the Phase 25 disclosure catalogue.
            </div>
          </div>
        </div>

        {/* Partner picker */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <label className="text-[11px] font-bold uppercase text-slate-500">
            Active partner
          </label>
          <select
            data-testid="ph29-partner-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-1 block rounded-md border-slate-300 text-sm min-w-[320px]"
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.business_name} (${p.partner_slug})`}
              </option>
            ))}
          </select>
          {partners.length === 0 ? (
            <div
              data-testid="ph29-no-eligible-partners"
              className="mt-2 text-xs text-amber-700"
            >
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              No active PWS partners found.
            </div>
          ) : null}
        </div>

        {loadingPack ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
          </div>
        ) : pack ? (
          <PackBody pack={pack} qrPngHref={qrPngHref} qrSvgHref={qrSvgHref} />
        ) : null}
      </div>
    </PortalShell>
  );
}


function PackBody({ pack, qrPngHref, qrSvgHref }) {
  return (
    <div
      data-testid={`ph29-pack-${pack.partner.id}`}
      className="space-y-7"
    >
      {/* Eligibility badge */}
      {!pack.eligible ? (
        <div
          data-testid="ph29-ineligible-banner"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          This partner is currently <strong>ineligible</strong> (status:{" "}
          {pack.partner.pws_partner_status || "unknown"}). Pack is shown
          for review only.
        </div>
      ) : null}

      {pack.sections.map((sec) => (
        <section
          key={sec.slug}
          data-testid={`ph29-section-${sec.slug}`}
          className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">
              {sec.title}
            </h2>
            <span className="text-[10px] font-mono text-slate-400">
              {sec.slug}
            </span>
          </div>

          {sec.body ? (
            <ul className="space-y-2 text-sm text-slate-700">
              {sec.body.map((line, i) => (
                <li
                  key={i}
                  data-testid={`ph29-section-${sec.slug}-line-${i}`}
                  className="flex gap-2"
                >
                  <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {sec.slug === "your_partner_booking_link" && sec.booking_url ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <code
                data-testid="ph29-booking-url"
                className="text-xs text-slate-800 font-mono break-all"
              >
                {sec.booking_url}
              </code>
              <button
                data-testid="ph29-copy-booking-url"
                onClick={() => copyToClipboard(sec.booking_url)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          ) : null}

          {sec.slug === "your_qr_code" && qrPngHref ? (
            <div className="flex flex-wrap items-center gap-4 mt-2">
              <img
                data-testid="ph29-qr-image"
                src={qrPngHref}
                alt={sec.qr_caption || "Partner QR code"}
                className="w-32 h-32 border border-slate-200 rounded-lg bg-white"
              />
              <div className="space-y-1 text-xs text-slate-600">
                <a
                  data-testid="ph29-download-qr-png"
                  href={qrPngHref}
                  download="partner-qr.png"
                  className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900"
                >
                  <ImageIcon className="w-3 h-3" /> Download PNG
                </a>
                {qrSvgHref ? (
                  <div>
                    <a
                      data-testid="ph29-download-qr-svg"
                      href={qrSvgHref}
                      download="partner-qr.svg"
                      className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900"
                    >
                      <FileText className="w-3 h-3" /> Download SVG
                    </a>
                  </div>
                ) : null}
                <div className="text-slate-500 mt-1">
                  {sec.qr_caption}
                </div>
              </div>
            </div>
          ) : null}

          {sec.scripts ? (
            <div className="space-y-3 mt-2">
              {sec.scripts.map((script, i) => {
                const Icon = SCRIPT_ICON[script.channel] || FileText;
                return (
                  <div
                    key={i}
                    data-testid={`ph29-script-${script.channel}`}
                    className="rounded-xl bg-slate-50 border border-slate-200 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                        <Icon className="w-3 h-3" /> {script.channel.replace("_", " ")}
                      </div>
                      <button
                        data-testid={`ph29-copy-script-${script.channel}`}
                        onClick={() => copyToClipboard(script.body)}
                        className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                      {script.body}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : null}

          {sec.slug === "what_not_to_say" ? (
            <div className="space-y-2 mt-2">
              {sec.intro ? (
                <p
                  data-testid="ph29-what-not-to-say-intro"
                  className="text-xs text-slate-600 italic"
                >
                  {sec.intro}
                </p>
              ) : null}
              <ul className="space-y-2">
                {(sec.items || []).map((it, i) => (
                  <li
                    key={i}
                    data-testid={`ph29-do-not-say-${i}`}
                    className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5"
                  >
                    <div className="text-xs font-bold text-rose-900">
                      DO NOT SAY: &ldquo;{it.forbidden_claim}&rdquo;
                    </div>
                    <div className="text-xs text-rose-700 mt-0.5">
                      {it.reason}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {sec.slug === "operational_checklist" && sec.items ? (
            <ul className="space-y-2 mt-2">
              {sec.items.map((it, i) => (
                <li
                  key={i}
                  data-testid={`ph29-checklist-${i}`}
                  className="flex gap-2 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600"
                    disabled
                  />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {sec.slug === "helpful_links" && sec.links ? (
            <ul className="space-y-2 mt-2">
              {sec.links.map((lk, i) => (
                <li key={i} className="text-sm">
                  <a
                    data-testid={`ph29-link-${i}`}
                    href={lk.href}
                    className="text-cyan-700 hover:text-cyan-900 underline"
                  >
                    {lk.label}
                  </a>
                  {lk.admin_only ? (
                    <span className="ml-2 text-[10px] uppercase font-bold text-slate-400">
                      admin
                    </span>
                  ) : null}
                </li>
              ))}
              {sec.contact ? (
                <li
                  data-testid="ph29-contact"
                  className="mt-3 text-xs text-slate-500"
                >
                  {sec.contact.team} —{" "}
                  <a
                    className="text-cyan-700 hover:text-cyan-900"
                    href={`mailto:${sec.contact.email}`}
                  >
                    {sec.contact.email}
                  </a>
                </li>
              ) : null}
            </ul>
          ) : null}
        </section>
      ))}
    </div>
  );
}
